/**
 * Losi-eye game glyphs — match the sketch: paddles / enemies are vertical
 * capsules (the eye silhouette), not rectangles or face-circles.
 */

const INK = '#0a0a0c'
const PAPER = '#f4f4f5'

/** Vertical capsule = one Losi eye. Angle 0 = upright. */
export function drawEyeCapsule(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  halfH: number,
  halfW: number,
  angle = 0,
  fill = INK
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.fillStyle = fill
  ctx.beginPath()
  const r = halfW
  const top = -halfH + r
  const bot = halfH - r
  ctx.moveTo(-r, top)
  ctx.arc(0, top, r, Math.PI, 0)
  ctx.lineTo(r, bot)
  ctx.arc(0, bot, r, 0, Math.PI)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

/** Small solid ball / bullet. */
export function drawInkDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  fill = INK
) {
  ctx.fillStyle = fill
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
}

/**
 * Player ship from the sketch: rounded V / twin-lobe chevron.
 * `angle` in radians; sketch note is ±30° lean while steering.
 */
export function drawChevronShip(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  angle = 0,
  fill = INK
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.fillStyle = fill
  ctx.beginPath()
  // Tip up (−y): nose toward enemies / flight direction at angle 0.
  ctx.moveTo(0, -size * 0.55)
  ctx.quadraticCurveTo(size * 0.55, -size * 0.1, size * 0.48, size * 0.45)
  ctx.quadraticCurveTo(size * 0.18, size * 0.15, 0, size * 0.05)
  ctx.quadraticCurveTo(-size * 0.18, size * 0.15, -size * 0.48, size * 0.45)
  ctx.quadraticCurveTo(-size * 0.55, -size * 0.1, 0, -size * 0.55)
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

/** Four-lobe sparkle outline (same waist formula as customiser `etoile`). */
export function sparkleRadius(angle: number, scale: number): number {
  const SPARKLE_WAIST = 0.39
  return scale * (1 - SPARKLE_WAIST * Math.sin(2 * angle) ** 2) * 1.04
}

export function drawSparklePath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  scale: number,
  samples = 64
) {
  ctx.beginPath()
  for (let i = 0; i <= samples; i++) {
    const a = (i / samples) * Math.PI * 2 - Math.PI / 2
    const r = sparkleRadius(a + Math.PI / 2, scale)
    const x = cx + Math.cos(a) * r
    const y = cy + Math.sin(a) * r
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.closePath()
}

/** Wider three-lobe enemy from the sketch. */
export function drawLobeEnemy(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  lobes = 3,
  fill = INK
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.fillStyle = fill
  ctx.beginPath()
  const n = 24
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * Math.PI * 2
    const wobble = 1 - 0.32 * Math.sin((lobes / 2) * t) ** 2
    const px = Math.cos(t - Math.PI / 2) * r * wobble
    const py = Math.sin(t - Math.PI / 2) * r * wobble
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

/** Face glyph kept for pinball ball flair (optional). */
export function drawLosiEyes(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  gazeX = 0,
  gazeY = 0
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.fillStyle = INK
  ctx.beginPath()
  ctx.arc(0, 0, r, 0, Math.PI * 2)
  ctx.fill()
  const ex = gazeX * r * 0.22
  const ey = gazeY * r * 0.18
  const er = r * 0.18
  const gap = r * 0.32
  ctx.fillStyle = PAPER
  ctx.beginPath()
  ctx.ellipse(-gap + ex, ey, er * 0.7, er, 0, 0, Math.PI * 2)
  ctx.ellipse(gap + ex, ey, er * 0.7, er, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export { INK, PAPER }
